import React, { useState } from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Target, Loader2, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, FileText, Code, Lightbulb } from "lucide-react";
import { Button } from "../ui/button";

export function IssuesAndFixesList({ scan, resultsLoading, selectedIssueId, onAskAdviser }) {
  const isProcessing = scan?.section_statuses?.issues?.status === "processing";
  const isFailed = scan?.section_statuses?.issues?.status === "failed";
  const issues = scan?.issues || [];
  const recommendations = scan?.recommendations || [];

  const [expandedIssues, setExpandedIssues] = useState({});

  const toggleExpand = (id) => {
    setExpandedIssues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (resultsLoading || (isProcessing && !["completed", "partial", "failed"].includes(scan?.status) && !issues.length)) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <Loader2 className="h-8 w-8 text-[#FEAB3D] animate-spin mb-4" />
        <p className="font-medium text-slate-700">Analyzing Issues & Formulating Fixes…</p>
        <p className="text-sm text-slate-500 mt-2">
          Deep scanning your pages for SEO and technical issues.
        </p>
      </Card>
    );
  }

  if (isFailed && !issues.length) {
    return (
      <Card className="p-8 text-center bg-red-50 border-red-200">
        <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
        <p className="font-medium text-red-700">Failed to generate Issues</p>
        <p className="text-sm text-red-600 mt-1">Please retry this section.</p>
      </Card>
    );
  }

  if (!issues.length) {
    return (
      <Card className="p-8 text-center text-slate-500">
        {["completed", "partial", "failed"].includes(scan?.status)
          ? "No actionable findings are available from the analyzed pages. This does not confirm that the site has no issues."
          : "No issues detected yet, or deep scan is still processing in the background."}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {issues.map((issue) => {
        const rec = recommendations.find(r => r.issue_id === issue.id);
        const isCritical = (issue.priority || "medium").toLowerCase() === "critical" || (issue.priority || "medium").toLowerCase() === "high";
        const severityColor = isCritical ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50";
        const isExpanded = expandedIssues[issue.id] ?? (selectedIssueId === issue.id);

        return (
          <Card key={issue.id} className={`overflow-hidden transition-all duration-300 border ${isCritical ? 'border-red-300' : 'border-amber-300'}`}>
            <div
              className={`p-5 cursor-pointer ${severityColor} hover:bg-opacity-80 transition-colors`}
              onClick={() => toggleExpand(issue.id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <AlertTriangle className={`h-5 w-5 ${isCritical ? "text-red-500" : "text-amber-500"}`} />
                    <span className="font-bold text-slate-800 text-base">{issue.title}</span>
                    <Badge variant="outline" className={`text-xs capitalize ${isCritical ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-700 border-amber-300'}`}>
                      {issue.priority} Priority
                    </Badge>
                    <Badge variant="secondary" className="text-xs bg-white text-slate-600 border border-slate-200">
                      {issue.category}
                    </Badge>
                    {issue.confidence && (
                      <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-500 border border-slate-200">
                        Confidence: {issue.confidence}
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-slate-700 mb-3 pr-4">{issue.explanation}</p>

                  {/* Summary Footer */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2 pt-3 border-t border-slate-200/50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500">Affected Page:</span>
                      <span className="text-xs text-[#FEAB3D] font-medium truncate max-w-[250px]" title={issue.affected_url}>
                        {issue.affected_url || "Site-wide"}
                      </span>
                    </div>
                    {rec && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-500">Impact:</span>
                          <span className="text-xs text-slate-700 capitalize">{rec.impact}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-500">Effort:</span>
                          <span className="text-xs text-slate-700 capitalize">{rec.effort}</span>
                        </div>
                      </>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500">Evidence:</span>
                      <span className="text-xs text-slate-700 italic truncate max-w-[300px]">"{issue.evidence}"</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {onAskAdviser && <Button variant="outline" size="sm" onClick={e => { e.stopPropagation(); onAskAdviser(issue); }}>Ask Adviser</Button>}
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-slate-700" onClick={(e) => { e.stopPropagation(); toggleExpand(issue.id); }}>
                    {isExpanded ? (
                      <><span className="mr-1 text-xs font-medium">Hide Fix</span><ChevronUp className="h-4 w-4" /></>
                    ) : (
                      <><span className="mr-1 text-xs font-medium">View Exact Fix</span><ChevronDown className="h-4 w-4" /></>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Expandable Exact Fix Section */}
            {isExpanded && rec && (
              <div className="bg-white dark:bg-slate-950 p-6 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-100">
                  <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-lg">Recommended Fix</h4>
                    <p className="text-sm text-slate-500">{rec.recommended_action}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {/* WHAT & WHERE */}
                  <div className="space-y-6">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <h5 className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        <Target className="h-4 w-4 text-[#FEAB3D]" /> What to change
                      </h5>
                      <p className="text-sm text-slate-700 leading-relaxed">{rec.what_to_change}</p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <h5 className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        <FileText className="h-4 w-4 text-[#FEAB3D]" /> Where to change it
                      </h5>
                      <p className="text-sm text-slate-700 leading-relaxed">{rec.where_to_change}</p>
                    </div>
                  </div>

                  {/* WHY & HOW */}
                  <div className="space-y-6">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <h5 className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        <Lightbulb className="h-4 w-4 text-amber-500" /> Why it matters
                      </h5>
                      <p className="text-sm text-slate-700 leading-relaxed">{rec.why_it_matters}</p>
                    </div>

                    <div className="bg-[#FEAB3D]/10 rounded-lg p-4 border border-[#FEAB3D]/20">
                      <h5 className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        <Code className="h-4 w-4 text-[#FEAB3D]" /> How to implement
                      </h5>
                      <p className="text-sm text-slate-700 leading-relaxed font-medium">{rec.how_to_implement}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Suggestions */}
                {rec.additional_suggestions && Object.keys(rec.additional_suggestions).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <h5 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FEAB3D]"></span>
                      Specific AI Suggestions
                    </h5>
                    <div className="grid grid-cols-1 gap-3">
                      {Object.entries(rec.additional_suggestions).map(([key, value]) => {
                        if (!value) return null;
                        return (
                          <div key={key} className="flex flex-col md:flex-row md:items-start gap-2 bg-slate-50 p-3 rounded-md border border-slate-200/60">
                            <span className="text-xs font-semibold text-slate-500 uppercase min-w-[120px] pt-0.5">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm text-slate-800 font-mono bg-white px-2 py-1 rounded border border-slate-200 flex-1">
                              {typeof value === "object" ? JSON.stringify(value, null, 2) : value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
